import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { AtUri } from '@atproto/api'

export interface LightboxTile {
  postUri: string
  src: string
  alt?: string
  isVideo: boolean
  isText?: boolean
  text?: string
  playlistUrl?: string
  fullsize?: string
  source: string
  isAdult: boolean
  authorHandle?: string
  whenIso?: string
}

interface Props {
  tiles: LightboxTile[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
  blurAdult: boolean
}

function bskyPostUrl(uri: string): string | null {
  try {
    const at = new AtUri(uri)
    if (at.collection !== 'app.bsky.feed.post') return null
    return `https://bsky.app/profile/${at.hostname}/post/${at.rkey}`
  } catch {
    return null
  }
}

export function ImageLightbox({ tiles, index, onIndex, onClose, blurAdult }: Props) {
  const tile = tiles[index]
  const [reveal, setReveal] = useState(false)
  useEffect(() => {
    setReveal(false)
  }, [index, tile?.postUri])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') onIndex(Math.max(0, index - 1))
      else if (e.key === 'ArrowRight') onIndex(Math.min(tiles.length - 1, index + 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, tiles.length, onIndex, onClose])

  if (!tile) return null
  const link = bskyPostUrl(tile.postUri)
  const blurred = blurAdult && tile.isAdult && !reveal

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="absolute left-0 right-0 top-0 flex flex-wrap items-center gap-2 p-3 text-xs text-ink-200"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5">
          {index + 1} / {tiles.length}
        </span>
        <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 uppercase">
          {tile.source}
        </span>
        {tile.isAdult ? (
          <span className="rounded-full border border-rose-400/40 bg-rose-500/20 px-2 py-0.5 font-bold text-rose-100">
            18+
          </span>
        ) : null}
        {tile.authorHandle ? (
          <span className="text-ink-300">@{tile.authorHandle}</span>
        ) : null}
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 hover:bg-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            open on bsky ↗
          </a>
        ) : null}
        {!tile.isText ? (
          <a
            href={tile.src}
            target="_blank"
            rel="noreferrer"
            className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 hover:bg-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            fullsize ↗
          </a>
        ) : null}
        <button
          type="button"
          className="ml-auto rounded-full border border-white/10 bg-white/5 px-2 py-0.5 hover:bg-white/10"
          onClick={onClose}
        >
          ✕ close
        </button>
      </div>

      <button
        type="button"
        className={clsx(
          'absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-black/60 px-3 py-2 text-xl text-white hover:bg-black/80',
          index === 0 && 'opacity-30',
        )}
        disabled={index === 0}
        onClick={(e) => {
          e.stopPropagation()
          onIndex(Math.max(0, index - 1))
        }}
        aria-label="previous"
      >
        ‹
      </button>

      <div
        className="flex max-h-[90vh] max-w-[90vw] items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {tile.isText ? (
          <div
            className={clsx(
              'max-h-[90vh] max-w-[90vw] overflow-auto rounded-2xl border border-white/15 bg-ink-900/80 p-6 text-base leading-relaxed text-ink-100 shadow-xl',
              blurred && 'blur-2xl',
            )}
            style={{ width: 'min(72ch, 90vw)' }}
            onClick={() => {
              if (blurred) setReveal(true)
            }}
          >
            <p className="whitespace-pre-wrap break-words">
              {tile.text ?? tile.alt ?? '(empty post)'}
            </p>
          </div>
        ) : tile.isVideo ? (
          tile.playlistUrl ? (
            <video
              src={tile.playlistUrl}
              poster={tile.src}
              controls
              playsInline
              autoPlay
              loop
              className="max-h-[90vh] max-w-[90vw] rounded-md"
            />
          ) : (
            <img
              src={tile.src}
              alt={tile.alt ?? ''}
              className="max-h-[90vh] max-w-[90vw] rounded-md object-contain"
              referrerPolicy="no-referrer"
            />
          )
        ) : tile.fullsize && /\.(mp4|webm)(\?|$)/i.test(tile.fullsize) ? (
          <video
            src={tile.fullsize}
            poster={tile.src}
            autoPlay
            loop
            muted
            playsInline
            controls
            className="max-h-[90vh] max-w-[90vw] rounded-md"
          />
        ) : (
          <img
            src={tile.fullsize ?? tile.src}
            alt={tile.alt ?? ''}
            className={clsx(
              'max-h-[90vh] max-w-[90vw] cursor-pointer rounded-md object-contain',
              blurred && 'blur-2xl',
            )}
            onClick={() => {
              if (blurred) setReveal(true)
            }}
            referrerPolicy="no-referrer"
          />
        )}
      </div>

      <button
        type="button"
        className={clsx(
          'absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-black/60 px-3 py-2 text-xl text-white hover:bg-black/80',
          index === tiles.length - 1 && 'opacity-30',
        )}
        disabled={index === tiles.length - 1}
        onClick={(e) => {
          e.stopPropagation()
          onIndex(Math.min(tiles.length - 1, index + 1))
        }}
        aria-label="next"
      >
        ›
      </button>

      {tile.alt ? (
        <div
          className="absolute bottom-3 left-1/2 max-w-[80vw] -translate-x-1/2 rounded-xl border border-white/10 bg-black/70 px-3 py-2 text-xs text-ink-100"
          onClick={(e) => e.stopPropagation()}
        >
          {tile.alt}
        </div>
      ) : null}
    </div>
  )
}
